import { BadgeTypes, QuestionToPlay } from '@sapphicsavvy/business';
import React, { ReactNode, useCallback, useState } from 'react';
import { View, ViewProps } from 'react-native';
import { initialWindowMetrics } from 'react-native-safe-area-context';
import StarIcon from '../resources/svgComponents/StarIcon';
import GlowingStarIcon from '../resources/svgComponents/Badges/GlowingStarIcon';
import SpiralCalendarIcon from '../resources/svgComponents/Badges/SpiralCalendarIcon';
import LoudsSpeakerIcon from '../resources/svgComponents/Badges/LoudsSpeakerIcon';
import PaperIcon from '../resources/svgComponents/Badges/PaperIcon';
import BullsEyeIcon from '../resources/svgComponents/Badges/BullsEyeIcon';
import MilitaryModalIcon from '../resources/svgComponents/Badges/MilitaryModalIcon';
import GameControlIcon from '../resources/svgComponents/GameControlIcon';
import GameControlIconGold from '../resources/svgComponents/GameControlIconGold';

export function createReactComponent<P>(Component: React.ComponentType<P>) {
    class ReactComponent extends React.Component<P> {
        render(): React.ReactNode {
            return <Component {...this.props} />;
        }
    }
    return ReactComponent;
}

export const hitSlop = { top: 10, bottom: 10, left: 10, right: 10 };

export function useModalState<T>(initialState?: T) {
    const [state, setState] = useState(initialState);

    return {
        state,
        show: useCallback((updateState: T) => setState(updateState), []),
        hide: useCallback(() => setState(undefined), []),
    };
}

export function getTopInset() {
    return initialWindowMetrics?.insets.top || 0;
}

export function ShouldSetResponderWrapper(props: ViewProps) {
    // if (Platform.OS === 'android') {
    //     return (
    //         <TouchableOpacity {...props} activeOpacity={1} />
    //     );
    // }
    return <View {...props} onStartShouldSetResponder={() => true} />;
}

export default function renderRandomStars(
    points: [number | string, number | string, number][],
) {
    return (
        <React.Fragment>
            {points.map((point) => (
                <View
                    key={JSON.stringify(point)}
                    style={{
                        position: 'absolute',
                        top: point[1],
                        left: point[0],
                    }}
                >
                    <StarIcon size={point[2]} />
                </View>
            ))}
        </React.Fragment>
    );
}

export function getNumberOfSteps(question: QuestionToPlay) {
    return (
        3 +
        (question?.identityGroupBonus ? 1 : 0) +
        (question?.hasIdealBonus ? 1 : 0)
    );
}

export function NotNull<T>(value?: T | undefined | null) {
    return value !== undefined && value !== null;
}

export function capitalize(s: string) {
    return s[0].toUpperCase() + s.slice(1);
}

export function numberWithCommas(number: number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function badgeToString(badgeType: BadgeTypes) {
    const badgeTypeStrings = {
        [BadgeTypes.idealAnswer]: 'Ideal Answer',
        [BadgeTypes.playStreaker]: 'Play Streaker',
        [BadgeTypes.prizeWinner]: 'Prize Winner',
        [BadgeTypes.miniGameWinner]: 'Game Winner',
        [BadgeTypes.top100Scorer]: 'Top 100 Scorer',
        [BadgeTypes.topQuestionSubmitter]: 'Top Question Submitter',
        [BadgeTypes.topReferrer]: 'Top Referrer',
    };

    return badgeTypeStrings[badgeType];
}

export const Badges: Record<BadgeTypes, ReactNode> = {
    [BadgeTypes.idealAnswer]: <GlowingStarIcon />,
    [BadgeTypes.playStreaker]: <SpiralCalendarIcon />,
    [BadgeTypes.topReferrer]: <LoudsSpeakerIcon />,
    [BadgeTypes.topQuestionSubmitter]: <PaperIcon />,
    [BadgeTypes.top100Scorer]: <BullsEyeIcon />,
    [BadgeTypes.prizeWinner]: <MilitaryModalIcon />,
    [BadgeTypes.miniGameWinner]: <GameControlIconGold />,
};

// ProfileBadges excludes topReferrer (replaced by miniGameWinner, no migration needed)
export const ProfileBadges: Partial<Record<BadgeTypes, ReactNode>> = {
    [BadgeTypes.idealAnswer]: <GlowingStarIcon size={40} />,
    [BadgeTypes.playStreaker]: <SpiralCalendarIcon size={40} />,
    [BadgeTypes.topQuestionSubmitter]: <PaperIcon size={40} />,
    [BadgeTypes.top100Scorer]: <BullsEyeIcon size={40} />,
    [BadgeTypes.prizeWinner]: <MilitaryModalIcon size={40} />,
    [BadgeTypes.miniGameWinner]: <GameControlIconGold size={40} />,
};

